import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import { toast } from 'react-toastify';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportPaymentsVsRefundsData,
} from '@src/types/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import { buildCommonPayloadWithGrouping } from '@src/utils/buildCommonPayload';
import {
  exportPaymentsVsRefundsReport,
  getPaymentsVsRefundsReport,
} from '@src/services/reports';
import { downloadFile } from '@src/lib/utils';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
    name: string;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="text-gray-300 text-xs">{entry.name}:</span>
            <span
              className="text-white font-semibold"
              style={{ color: entry.color }}
            >
              ${entry.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const PaymentsVsRefunds: FC = () => {
  // Data state
  const [data, setData] = useState<TReportPaymentsVsRefundsData[]>([]);

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
  });

  // Fixed display items for this report
  const displayItems = ['income', 'payment', 'refund'];

  // Use custom hook for table columns generation
  const { columns, tableData } = useReportTableColumns({
    data,
    selectedItems: displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => {
      switch (key) {
        case 'income':
          return item.income;
        case 'payment':
          return item.payment;
        case 'refund':
          return item.refund;
        default:
          return 0;
      }
    },
    getPreviousPeriodField: (item, key) => {
      switch (key) {
        case 'income':
          return item.income_previous;
        case 'payment':
          return item.payment_previous;
        case 'refund':
          return item.refund_previous;
        default:
          return undefined;
      }
    },
    getPreviousYearField: (item, key) => {
      switch (key) {
        case 'income':
          return item.income_previous_year;
        case 'payment':
          return item.payment_previous_year;
        case 'refund':
          return item.refund_previous_year;
        default:
          return undefined;
      }
    },
    valueFormatter: (_, value: number) => `$${value.toLocaleString()}`,
    includeTotalsRow: true,
  });

  // Use custom hook for chart data transformation
  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => {
      switch (key) {
        case 'income':
          return item.income;
        case 'payment':
          return item.payment;
        case 'refund':
          return item.refund;
        default:
          return 0;
      }
    },
    getPreviousPeriodField: (item, key) => {
      switch (key) {
        case 'income':
          return item.income_previous;
        case 'payment':
          return item.payment_previous;
        case 'refund':
          return item.refund_previous;
        default:
          return undefined;
      }
    },
    getPreviousYearField: (item, key) => {
      switch (key) {
        case 'income':
          return item.income_previous_year;
        case 'payment':
          return item.payment_previous_year;
        case 'refund':
          return item.refund_previous_year;
        default:
          return undefined;
      }
    },
  });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildCommonPayloadWithGrouping(filters);
      const response = await getPaymentsVsRefundsReport(payload);

      setData(response);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Failed to generate report. Please try again.');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const payload = buildCommonPayloadWithGrouping(filters);
      const response = await exportPaymentsVsRefundsReport(payload, 'csv');

      downloadFile(response.data, 'payments-vs-refunds-report');
      toast.success('Report exported successfully!');
    } catch (error) {
      toast.error('Failed to export report. Please try again.');
      console.error('Error exporting report:', error);
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">Payments vs Refunds</h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="line"
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
              />

              {/* Main data lines */}
              {displayItems.map((metric, index) => {
                const label =
                  metric.charAt(0).toUpperCase() + metric.slice(1) + ', USD';

                return (
                  <Line
                    key={metric}
                    activeDot={{ r: 6 }}
                    dataKey={metric}
                    dot={{ fill: getChartColor(index), r: 4 }}
                    name={label}
                    stroke={getChartColor(index)}
                    strokeWidth={2}
                    type="monotone"
                  />
                );
              })}

              {/* Previous period lines */}
              {filters.compareToPreviousPeriod &&
                displayItems.map((metric, index) => {
                  const label =
                    metric.charAt(0).toUpperCase() +
                    metric.slice(1) +
                    ', USD (Previous Period)';

                  return (
                    <Line
                      key={`${metric}_previous_period`}
                      activeDot={{ r: 4 }}
                      dataKey={`${metric}_previous_period`}
                      dot={{
                        fill: getChartColor(index),
                        r: 3,
                      }}
                      name={label}
                      stroke={getChartColor(index)}
                      strokeDasharray="5 5"
                      strokeWidth={1.5}
                      type="monotone"
                    />
                  );
                })}

              {/* Previous year lines */}
              {filters.compareToSamePeriodPreviousYear &&
                displayItems.map((metric, index) => {
                  const label =
                    metric.charAt(0).toUpperCase() +
                    metric.slice(1) +
                    ', USD (Previous Year)';

                  return (
                    <Line
                      key={`${metric}_previous_year`}
                      activeDot={{ r: 4 }}
                      dataKey={`${metric}_previous_year`}
                      dot={{
                        fill: getChartColor(index),
                        r: 3,
                      }}
                      name={label}
                      stroke={getChartColor(index)}
                      strokeDasharray="2 2"
                      strokeWidth={1.5}
                      type="monotone"
                    />
                  );
                })}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={handleExportReport}
            >
              Download CSV
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
